import { getBranches } from '@/apis/branches';
import { MEMBERSHIP_STATUSES_WITH_IDS } from '@/constants/filterCriteria/membership';
import { AutocompleteResult } from '@/types';
import { MembershipRecordResult } from '@/types/filterCriteriaRecords';
import { NotRenewingType } from '@/types/shared';
import { labelSorting } from '@/util/labelSorting';
import {
  FetchAutocomplete,
  FetchAutocompleteChangeReason,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionField } from '@components/DrawerAccordion';
import { Search } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { useAtomValue } from 'jotai';

type MembershipStatusFormProps = {
  onChangeMemberships: (
    newValue: MembershipRecordResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  membershipsValue: MembershipRecordResult[];
  onChangeBranches?: (
    newValue: AutocompleteResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  branchesValue: AutocompleteResult[];
  notRenewing: NotRenewingType;
  onChangeNotRenewing?: (notRenewingValue: NotRenewingType) => void;
};

const MembershipStatusForm = ({
  membershipsValue = [] as MembershipRecordResult[],
  onChangeMemberships = () => {},
  branchesValue = [] as AutocompleteResult[],
  onChangeBranches = () => {},
  notRenewing = '',
  onChangeNotRenewing = () => {},
}: MembershipStatusFormProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const domainState =
    !domainCode || ['ag', 'fedmps'].includes(domainCode) ? '' : domainCode.toUpperCase();

  const lookupBranches = async (_lookupValue: string) => {
    const token = await getAccessTokenSilently();
    return (await getBranches(token, domainState, _lookupValue))
      .map((x) => {
        return {
          id: x.value,
          label: domainState === '' ? x.state + ' - ' + x.label : x.label,
        };
      })
      .sort(labelSorting);
  };

  const handleChangeNotRenewing = (event: SelectChangeEvent) => {
    const selectValue = event.target.value as NotRenewingType;
    onChangeNotRenewing(selectValue);
  };

  return (
    <AccordionField>
      <FetchAutocomplete
        minLength={0}
        label="Select a membership status"
        preLoadedOptions={MEMBERSHIP_STATUSES_WITH_IDS}
        value={membershipsValue}
        onChange={onChangeMemberships}
        data-testid="membershipstatuses"
        loadingText="Looking for membership statuses..."
        noOptionsText="No membership statuses found"
      />
      {membershipsValue.length !== 0 && (
        <>
          <p style={{ margin: '8px' }} />
          <FetchAutocomplete
            minLength={3}
            lookup={lookupBranches}
            label="Select a branch (optional)"
            value={branchesValue}
            onChange={onChangeBranches}
            data-testid="branches"
            loadingText="Looking for branches statuses..."
            noOptionsText="No branches found"
            popupIcon={<Search />}
            disableIconFlip
          />

          <Grid container spacing={2} sx={{ marginTop: '8px' }}>
            <Grid
              size={6}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title='Set to "Exclude" to omit people who do not intend to renew'>
                <InputLabel id="membership-notRenewing-label" sx={{ color: 'black' }}>
                  Not Renewing (optional)
                </InputLabel>
              </Tooltip>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <Select
                  labelId="membership-notRenewing-label"
                  value={notRenewing}
                  onChange={handleChangeNotRenewing}
                  data-testid="notRenewing"
                >
                  <MenuItem value={''}>&nbsp;</MenuItem>
                  <MenuItem value={'exclude'}>Exclude</MenuItem>
                  <MenuItem value={'onlyInclude'}>Only include</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
    </AccordionField>
  );
};

export default MembershipStatusForm;
